import { useState } from 'react';

import { EntitySelect } from '@/components/EntitySelect';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ApiError } from '@/core/api';
import { useCreateCompany } from '@/features/crm/companies/queries';
import { resolveCompanyLabel, searchCompanies } from '@/features/crm/companies/select';
import { cn } from '@/lib/utils';
import { useConvertLead, useUpdateLead } from '../queries';
import type { Lead } from '../types';

/**
 * A deal is always pipeline on a Company (locked data spine), so a lead
 * without one can't convert directly. This dialog closes the gap in one
 * motion: pick an existing company, or create one — for individual clients
 * the Upwork convention applies: the "company" is just the person's name,
 * for records only (prefilled from the lead's contact when there is one).
 */
export function ConvertLeadDialog({
  open,
  onOpenChange,
  lead,
  suggestedCompanyName,
  onConverted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lead: Lead;
  /** Prefill for the new-company name — the lead's contact ("First Last"). */
  suggestedCompanyName?: string | null;
  onConverted: (dealPublicId: string) => void;
}) {
  const createCompany = useCreateCompany();
  const updateLead = useUpdateLead(lead.id);
  const convert = useConvertLead();

  const [mode, setMode] = useState<'existing' | 'new'>(
    suggestedCompanyName ? 'new' : 'existing',
  );
  const [companyId, setCompanyId] = useState('');
  const [name, setName] = useState(suggestedCompanyName ?? '');
  const [error, setError] = useState<string | null>(null);

  const busy = createCompany.isPending || updateLead.isPending || convert.isPending;
  const canSubmit = mode === 'existing' ? companyId !== '' : name.trim() !== '';

  async function handleConvert() {
    setError(null);
    try {
      let company = mode === 'existing' ? Number(companyId) : null;
      if (mode === 'new') {
        const created = await createCompany.mutateAsync({
          name: name.trim(),
          website: '',
          address: '',
          country: '',
          notes: '',
          is_active: true,
        });
        company = created.id;
      }
      await updateLead.mutateAsync({ company });
      const deal = await convert.mutateAsync({ id: lead.id });
      onOpenChange(false);
      onConverted(deal.public_id);
    } catch (e) {
      const err = e as ApiError;
      setError(
        err?.fieldErrors ? Object.values(err.fieldErrors).flat().join(' ') : err?.message,
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Convert to deal</DialogTitle>
          <DialogDescription>
            A deal always belongs to a company. Pick one, or create it — for an
            individual client just use their name.
          </DialogDescription>
        </DialogHeader>

        <div
          role="group"
          aria-label="Company source"
          className="border-input bg-background inline-flex w-fit overflow-hidden rounded-md border"
        >
          {(
            [
              { value: 'existing', label: 'Existing company' },
              { value: 'new', label: 'New company' },
            ] as const
          ).map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setMode(value)}
              aria-pressed={mode === value}
              className={cn(
                'inline-flex h-9 items-center px-3 text-sm transition-colors',
                mode === value
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:bg-accent hover:text-foreground',
              )}
            >
              {label}
            </button>
          ))}
        </div>

        {mode === 'existing' ? (
          <div className="space-y-1.5">
            <Label>Company</Label>
            <EntitySelect
              value={companyId}
              onChange={setCompanyId}
              search={searchCompanies}
              resolveLabel={resolveCompanyLabel}
              placeholder="Search companies…"
              queryKey="convert-lead-company"
            />
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label htmlFor="convert-company-name">Company name</Label>
            <Input
              id="convert-company-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Acme Inc. — or the client's own name"
            />
          </div>
        )}

        {error ? <p className="text-destructive text-sm">{error}</p> : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={handleConvert} disabled={!canSubmit || busy}>
            {busy ? 'Converting…' : 'Convert to deal'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
